import moment from 'moment'

import { getCssVar } from './getCssVar'
import getAppEventID from '@/utils/getAppEventID'
import getTerminology from './getTerminology'

export const handleNumericKeyDown = (event) => {
	// We allow special keys: Backspace, Delete, left and right arrows
	if (
		event.key === 'Backspace' ||
		event.key === 'Delete' ||
		event.key === 'ArrowLeft' ||
		event.key === 'ArrowRight' ||
		event.key === 'Tab'
	) {
		return
	}
	// Prohibit all other non-numeric characters
	if (!/[0-9]/.test(event.key)) {
		event.preventDefault()
	}
}

export const handleNumericInputWithDot = (event) => {
	const { key, target } = event
	const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab']

	if (allowedKeys.includes(key)) {
		return
	}

	if (key === '.' && target.value.includes('.')) {
		event.preventDefault()
		return
	}

	if (key === ',') {
		event.preventDefault()
		return
	}

	if (!/[0-9.]/.test(key)) {
		event.preventDefault()
	}
}

export const truncateText = (text, maxLength) => {
	if (!text) return ''
	if (text.length > maxLength) {
		return text.slice(0, maxLength) + '...'
	}
	return text
}

export const formatWithThousandSeparators = (number) => {
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const formatDate = (dateString) => {
	if (!dateString) return ''

	return moment(dateString, 'DD-MM-YYYY HH:mm:ss').format('D MMMM YYYY')
}

export const changeFirstChar = (str, newChar = '') => {
	if (!str || typeof str !== 'string') {
		throw new Error('Invalid input: Please provide a valid string and a single character.')
	}
	return newChar + str.slice(1)
}

export const flattenMenu = (menu) => {
	return menu.reduce((acc, item) => {
		if (item.children && item.children.length > 0) {
			return acc.concat(item, flattenMenu(item.children))
		} else {
			return acc.concat(item)
		}
	}, [])
}

export const getWholePrice = (price) => {
	const str = String(price || '')
	if (!str) return ''

	// Remove decimal part if it's only zeros (e.g. .0, .00, .000)
	return str.replace(/\.0+$/, '')
}

export const convertFileToServerStructure = async (file, field, fileName) => {
	return new Promise((resolve, reject) => {
		if (!file) {
			reject('No file provided')
			return
		}

		const reader = new FileReader()

		reader.onload = () => {
			resolve({
				[field]: {
					name: file.name || fileName,
					size: file.size,
					type: file.type,
					data: reader.result.split(',')[1], // Remove the prefix ‘data:image/png;base64,’
				},
			})
		}

		reader.onerror = () => {
			reject('Error reading file')
		}

		reader.readAsDataURL(file)
	})
}

export const privacyPolicyAcceptCookieExpKey = () => {
	const eventId = getAppEventID()
	return `EMMA_V2_${eventId}_POLICY_ACCEPT_COOKIE_EXP`
}

export const toggleScrollLock = (bool, elements = []) => {
	const targets = [
		document.body,
		document.querySelector('#root'),
		...elements.map((el) => document.querySelector(el)),
	]

	targets.forEach((target) => {
		if (target) {
			target.style.overflow = bool ? 'hidden' : ''
		}
	})
}

export const setBackgroundColor = (selector, color) => {
	const element = document.querySelector(selector)
	if (element) {
		if (color) {
			element.style.backgroundColor = color
		} else {
			element.style.removeProperty('background-color')
		}
	} else {
		console.warn(`Element with selector "${selector}" not found.`)
	}
}

export const overrideColorByClass = (className, color) => {
	const elements = document.getElementsByClassName(className)
	Array.from(elements).forEach((el) => {
		el.style.setProperty('color', color, 'important') // Override even if original has !important
	})
}

export const preventCapturingTabFocus = (arrQS = []) => {
	arrQS.forEach((qs) => {
		document.querySelectorAll(qs).forEach((el) => {
			el?.setAttribute?.('tabindex', '-1') // Prevents it from capturing tab focus
		})
	})
}

export function parseNestedPayload(payload) {
	const parsed = {}

	for (const [key, value] of Object.entries(payload)) {
		const match = key.match(/^(\w+)\[(.+)\]$/)
		if (match) {
			const [, parentKey, innerKey] = match
			if (!parsed[parentKey]) {
				parsed[parentKey] = {}
			}
			parsed[parentKey][innerKey] = value
		} else {
			parsed[key] = value
		}
	}

	return parsed
}

/**
 * Checks if the user has no previous browser history.
 * @returns {boolean} True if user landed directly or refreshed the page (i.e., no back history).
 */
export function hasNoPreviousHistory() {
	return window.history.length <= 1
}

export const getRandomNumber = (min, max) => {
	return Math.floor(Math.random() * (max - min) + min)
}

export const getPhoneErrMsgByCode = (code) => {
	const errorMap = getTerminology('validation.mobile_errors', null, false) || []

	return errorMap[code] || ''
}

export const getHeaderHeightVar = () => Number(getCssVar(`header-height`).replace('px', ''))
